import classNames from "classnames";
import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import { OptionProps, components } from "react-select";
import AsyncSelect from "react-select/async";

import { IUserResponse } from "../../config/interfaces";
import { useAppSelector } from "../../redux/Store";
import { ITRFilingSelector } from "../../redux/slice/ITRFilingSlice";

interface Props {
  name: string;
  onSearch: (data: string) => any;
  onChange: (data: IUserResponse) => void;
  customContent: (_data: any) => JSX.Element;
  closeMenuOnSelect?: boolean;
  optionLabel?: (_option: any) => string;
  size?: "lg" | "sm";
  value?: any | null;
  containerClasses?: any;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  isClearable?: boolean;
  placeholder?: string;
  label?: string;
  isInvalid?: boolean;
  errorMsg?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  note?: string;
  formType?: "ITR" | "GENERAL";
}

const AsyncDropdownSelect: React.FC<Props> = (props: Props) => {
  const {
    name,
    isInvalid,
    isMulti = false,
    isSearchable = false,
    isDisabled = false,
    closeMenuOnSelect = true,
    placeholder = "Please select an option",
    errorMsg,
    note,
    label = "",
    onSearch,
    value = null,
    size = "sm",
    containerClasses,
    onChange,
    isClearable = false,
    optionLabel,
    customContent,
    onBlur,
    formType = "ITR"
  } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const isFieldDisabled = useMemo(() => {
    if (formType === "GENERAL") {
      if (isDisabled) {
        return isDisabled;
      }
      return false;
    }
    if (isFilingReadOnly) {
      return true;
    }
    return isDisabled;
  }, [isDisabled, isFilingReadOnly, formType]);

  const getOptions = (query: any) => {
    return new Promise<any[]>(async (resolve, reject) => {
      try {
        const filterData = await onSearch(query);
        return resolve(filterData);
      } catch (error) {
        console.error("Error fetching data:", error);
        reject(error);
      }
    });
  };

  const CustomOption = (props: OptionProps<any, false>) => {
    const { data } = props;

    return <components.Option {...props}>{customContent && customContent(data)}</components.Option>;
  };

  return (
    <>
      <Form.Group className="form-group" controlId={name}>
        {label ? <Form.Label className="small-label">{label}</Form.Label> : null}
        <AsyncSelect
          menuShouldScrollIntoView={false}
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          name={name}
          loadOptions={getOptions}
          value={value ? value : null}
          onChange={(_value) => onChange(_value)}
          placeholder={placeholder}
          isSearchable={isSearchable}
          isDisabled={isFieldDisabled}
          isClearable={isClearable}
          getOptionLabel={(option) => (optionLabel ? optionLabel(option) : "label")}
          getOptionValue={(option) => option.id}
          onBlur={onBlur}
          closeMenuOnSelect={closeMenuOnSelect}
          className={classNames(
            "eds-select",
            size && `eds-select-${size}`,
            isInvalid && "field-invalid",
            containerClasses
          )}
          classNamePrefix="eds-select"
          cacheOptions
          components={{ Option: CustomOption }}
          defaultOptions={true}
          blurInputOnSelect={false}
        />
        {note && !isInvalid && !errorMsg && <Form.Text className="field-text">{note}</Form.Text>}
        {isInvalid && errorMsg ? <div className="field-error">{errorMsg}</div> : null}
      </Form.Group>
    </>
  );
};

export default AsyncDropdownSelect;
